import { CompleteIndustryData, IndustryBottleneckSection, IndustryTechStackItem, TechItem } from './industryData';
import React from 'react';

// Re-export the interface for convenience
export type { CompleteIndustryData } from './industryData';

// FinTech & BFSI Industry Data
const finTechIndustry: CompleteIndustryData = {
  id: 'finance-banking',
  hero: {
    icon: '🏦',
    title: 'FinTech & BFSI Cloud Infrastructure Services',
    subtitle: 'Modern Banking Infrastructure',
    description: 'We build high-performance, secure financial infrastructure for mission-critical workloads. Our solutions ensure compliance, optimize costs, and deliver future-proof architecture for real-time payment systems.',
    highlights: [
      'SOC 2 Type I compliance in under 30 days',
      'Multi-cloud secure architectures for data localization',
      'Securing real-time payment infrastructure',
      'Reduce monthly cloud spend by 40% via targeted FinOps'
    ]
  },
  benefits: [
    "SOC 2 Type I compliance in under 30 days, Type II in 90 days",
    "Multi-cloud secure architectures for data localization compliance",
    "Securing real-time payment infrastructure",
    "Reduce monthly cloud spend by 40% via targeted FinOps"
  ],
  bottomCTA: {
    title: "Ready to Transform Your FinTech Infrastructure?",
    description: "Let's discuss how our expertise can accelerate your digital transformation journey."
  },
  components: {
    bottleneckSections: [
      {
        title: "Cloud Bottlenecks",
        icon: "☁️",
        items: [
          {
            title: "Latency-Optimized & Region-Aware Infrastructure",
            description: "We implement region-specific deployment topologies using local zones and edge locations for performance-sensitive APIs and trading systems. With data residency mandates from RBI, GDPR, and MAS, we design compliant and low-latency networks using services like AWS Global Accelerator, PrivateLink, GCP VPC Peering, and Azure Front Door to ensure geographic and regulatory alignment."
          },
          {
            title: "Cloud Adoption of Legacy Core Banking",
            description: "Betaque integrates mainframes using event-driven APIs and adapter services, enabling gradual refactoring. We establish secure hybrid networks using AWS Direct Connect, Azure Arc, and GCP Interconnect, with ETL pipelines syncing COBOL mainframes to Kafka."
          },
          {
            title: "Vendor Lock-in Concerns",
            description: "To support cloud agnosticism, we use Terraform/Terragrunt, cloud-agnostic secret managers, and unified audit trails via OpenTelemetry. Infrastructure normalization and multi-cloud KMS support ensure workload portability and consistent compliance posture."
          },
          {
            title: "High-Performance Financial Workloads",
            description: "We leverage optimized instance types (Graviton, C6i), provision local zones, and implement CDN-backed API layers. Fraud detection and real-time financial APIs are routed through private endpoints and accelerated with tuned ENIs."
          },
          {
            title: "Lack of FinOps Maturity",
            description: "We deploy cost optimization guardrails via Kubecost, Infracost, and FinOps dashboards. High-volume NAT gateway costs are minimized by routing traffic through AWS PrivateLink, VPC Interface Endpoints, and GCP VPC Peering, replacing unnecessary public egress."
          }
        ]
      },
      {
        title: "DevOps Bottlenecks",
        icon: "⚙️",
        items: [
          {
            title: "Strict Change Management Policies",
            description: "We integrate policy-based approvals in GitOps pipelines using ArgoCD with OPA/Kyverno gates. Preview environments are short-lived, fully audited, and scoped to reviewers."
          },
          {
            title: "Poor Automation in Governance",
            description: "Policy-as-Code across layers—Terraform Sentinel for IaC, Kyverno for Kubernetes, Checkov and Conftest for CI/CD. This guarantees enforcement of security tags, encryption, and ownership metadata."
          },
          {
            title: "Lack of Environment Parity",
            description: "We build golden images and Helm charts shared across dev/staging/prod with Kustomize overlays. CI pipelines validate state parity through snapshot tests and test suites."
          },
          {
            title: "Data Pipeline Fragility",
            description: "We introduce Airflow with SLA monitoring and checkpointing. All pipelines are versioned, lineage-tracked, and support rollback. Governance is enforced with Great Expectations and ML metadata versioning."
          },
          {
            title: "Developer Experience Under Constraints",
            description: "Scoped access using Vault and HashiCorp Boundary. Tokenized test environments replicate production auth/token flows without compromising sensitive data."
          }
        ]
      },
      {
        title: "Security Bottlenecks",
        icon: "🔐",
        items: [
          {
            title: "IAM Complexity and Role Explosion",
            description: "We reduce IAM surface by enforcing least privilege via CI checks and automated pruning. Tools like GCP Policy Analyzer, AWS Access Analyzer, and Azure IAM Insights are regularly audited."
          },
          {
            title: "Secrets Sprawl",
            description: "Centralized secret lifecycle via Vault, External Secrets, and GitHub Secrets scanning. Auto-rotate enabled and limited scope per environment and role."
          },
          {
            title: "Data-at-Rest and In-Transit Encryption Gaps",
            description: "Envelope encryption with KMS and BYOK where applicable. All data stores—S3, GCS, RDS, BigQuery—are encrypted and monitored for key lifecycle and deletion policies."
          },
          {
            title: "Third-party Risks",
            description: "All third-party packages scanned with Snyk, Trivy, and dependency checkers. Outbound connections are firewalled, and egress policies lock down data exfiltration paths."
          }
        ]
      }
    ],
    techStackItems: [
      {
        title: "Cloud & Infra",
        technologies: [
          { name: "AWS FSI-ready services", icon: "aws" },
          { name: "GCP Anthos", icon: "gcp" }, 
          { name: "Azure Confidential Compute", icon: "azure" }, 
          { name: "Direct Connect", icon: "connect" }, 
          { name: "PrivateLink", icon: "link" }, 
          { name: "VPC Peering", icon: "network" }
        ]
      },
      {
        title: "Infrastructure as Code",
        technologies: [
          { name: "Terraform", icon: "terraform" },
          { name: "Terragrunt", icon: "terragrunt" },
          { name: "Pulumi", icon: "pulumi" },
          { name: "Atlantis", icon: "atlantis" },
          { name: "OpenTofu", icon: "opentofu" }
        ]
      },
      {
        title: "CI/CD & GitOps",
        technologies: ["GitHub Actions", "GitLab CI", "ArgoCD", "Jenkins", "FluxCD", "Helm", "Kustomize", "Argo Rollouts"]
      },
      {
        title: "Security & Compliance",
        technologies: ["HashiCorp Vault", "OPA", "Kyverno", "Checkov", "AWS Config", "GCP SCC", "Azure Defender"]
      },
      {
        title: "Monitoring & FinOps",
        technologies: ["Prometheus", "Grafana", "ELK", "OpenTelemetry", "Infracost", "Kubecost"]
      }
    ]
  }
};

// Government & Public Sector Industry Data
const governmentIndustry: CompleteIndustryData = {
  id: 'government-public-sector',
  hero: {
    icon: '🏛️',
    title: 'Government & Public Sector Cloud Infrastructure Services',
    subtitle: 'Secure Public Service Systems',
    description: 'Secure, compliant infrastructure for citizen-facing services that meets strict sovereignty requirements. We deliver national-scale systems optimized for reliability, auditability, and performance.',
    highlights: [
      'Secure, compliant cloud-native systems for public sector missions',
      'Expertise in national-scale infrastructure deployments',
      'Cross-cloud and on-premise solutions with strong compliance posture',
      'Sovereign-grade deployments with open-source tooling'
    ]
  },
  benefits: [
    "Secure, compliant cloud-native systems for public sector missions",
    "Expertise in national-scale infrastructure deployments",
    "Cross-cloud and on-premise solutions with strong compliance posture",
    "Sovereign-grade deployments with open-source tooling"
  ],
  bottomCTA: {
    title: "Ready to Modernize Your Public Sector Infrastructure?",
    description: "Let's discuss how our expertise can support your mission-critical systems."
  },
  components: {
    bottleneckSections: [
      {
        title: "Cloud Bottlenecks",
        icon: "☁️",
        items: [
          {
            title: "Sovereign Cloud & Data Residency Compliance",
            description: "We architect infrastructure compliant with local data residency laws (e.g., MeitY, RBI, GDPR). Betaque deploys workloads to sovereign cloud zones, uses region-pinned storage with KMS-integrated encryption, and implements geo-fencing for sensitive workloads."
          },
          {
            title: "Legacy System Modernization",
            description: "We re-platform legacy applications from on-prem mainframes to modern Kubernetes or serverless architectures using AWS Outposts, Azure Stack, or GCP Anthos. Custom service mesh integrations ensure secure and observable hybrid deployments."
          },
          {
            title: "Inter-Agency Collaboration Infrastructure",
            description: "Betaque builds centralized APIs and multi-tenant microservices architectures to support state-wide e-governance and citizen service platforms. This reduces redundancy and boosts interoperability."
          }
        ]
      },
      {
        title: "DevOps Bottlenecks",
        icon: "⚙️",
        items: [
          {
            title: "Rigid Change Approval Workflows",
            description: "We implement GitOps-based CI/CD pipelines with ArgoCD, GitLab CI, and Jenkins to enable version-controlled infrastructure changes. Role-based approvals and audit logs satisfy regulatory oversight."
          },
          {
            title: "Environment Drift Across Departments",
            description: "We ensure consistency between dev, test, and prod with infrastructure as code (Terraform, Terragrunt) and policy-as-code (Kyverno, OPA). This ensures compliance across diverse project teams."
          }
        ]
      }
    ],
    techStackItems: [
      {
        title: "Cloud Platforms",
        technologies: ["AWS GovCloud", "Azure Government", "GCP Sovereign Cloud", "Anthos", "Azure Arc", "OpenShift", "Bare Metal Kubernetes"]
      },
      {
        title: "Infrastructure as Code",
        technologies: ["Terraform", "Terragrunt", "CloudFormation", "Pulumi"]
      },
      {
        title: "Security & Compliance",
        technologies: ["Vault", "External Secrets", "OPA", "Kyverno", "Checkov", "AWS Config", "Azure Policy"]
      }
    ],
    infraFeatures: [
      {
        title: "Kubernetes on Bare Metal",
        description: "Deploying OpenShift, Rancher, or kubeadm-managed Kubernetes clusters on bare metal within government-authorized DCs."
      },
      {
        title: "Self-Hosted Alternatives",
        description: "Implementing self-hosted alternatives for databases (PostgreSQL, MySQL), object storage (MinIO, Ceph), CI/CD (GitLab, Jenkins), and secret management (Vault)."
      },
      {
        title: "Open-Source Observability",
        description: "Using open-source observability stacks (Prometheus, ELK, Loki) and IDS systems (Suricata) for complete control over telemetry and intrusion detection."
      },
      {
        title: "Regulatory Compliance",
        description: "Ensuring compliance with MeitY, CERT-In, and NIC guidelines without relying on third-party cloud providers."
      }
    ]
  }
};

// Machine Learning & AI Industry Data
const mlaiIndustry: CompleteIndustryData = {
  id: 'machine-learning-ai',
  hero: {
    icon: '🧠',
    title: 'Machine Learning & AI Infrastructure Services',
    subtitle: 'Scalable Intelligence Platforms',
    description: 'GPU-optimized, cost-efficient infrastructure for AI-first products. We build resilient ML platforms with reproducible pipelines that let your team focus on models, not infrastructure.',
    highlights: [
      'Specialized in GPU-optimized and cost-efficient ML infrastructure',
      'Experience with multi-cloud ML platforms and model serving',
      'End-to-end MLOps pipelines with reproducibility and security',
      'Compliance automation for regulated AI applications'
    ]
  },
  benefits: [
    "Specialized in GPU-optimized and cost-efficient ML infrastructure",
    "Experience with multi-cloud ML platforms and model serving",
    "End-to-end MLOps pipelines with reproducibility and security",
    "Compliance automation for regulated AI applications"
  ],
  bottomCTA: {
    title: "Ready to Accelerate Your ML/AI Infrastructure?",
    description: "Let's discuss how our expertise can help you build scalable, cost-efficient ML platforms."
  },
  components: {
    bottleneckSections: [
      {
        title: "Cloud Bottlenecks",
        icon: "☁️",
        items: [
          {
            title: "GPU/TPU Quotas & Availability",
            description: "Regional job scheduling, preemption-aware GPU pools."
          },
          {
            title: "Cost-Efficient Training/Inference",
            description: "Spot instance automation, model warmup scalers."
          },
          {
            title: "Cross-Cloud Portability",
            description: "Dockerized model builds, artifact syncing, RClone for datasets."
          }
        ]
      },
      {
        title: "DevOps & MLOps Bottlenecks",
        icon: "⚙️",
        items: [
          {
            title: "No CI/CD for Models",
            description: "Argo + GitHub Actions + MLflow + Docker for end-to-end reproducibility."
          },
          {
            title: "Training Instability",
            description: "CUDA version mismatch, Hydra-based env configs."
          },
          {
            title: "Monitoring Gaps",
            description: "Integrated Prometheus dashboards, model scoring feedback loop."
          }
        ]
      }
    ],
    techStackItems: [
      {
        title: "Pipelines",
        technologies: ["Kubeflow", "Argo", "Airflow", "MLflow"]
      },
      {
        title: "Serving",
        technologies: ["KServe", "Triton", "Knative", "Seldon"]
      },
      {
        title: "Tracking",
        technologies: ["MLflow", "Neptune", "TensorBoard"]
      }
    ],
    mlOutcomes: [
      {
        title: "GPU-Optimized Training Pipelines for LLM Startup",
        emoji: "🚀",
        challenge: "Cost-intensive A100 training clusters with poor job scheduling.",
        solution: "GKE Autopilot + Kubeflow with spot-aware orchestration and auto-shutdown for idle GPUs.",
        impact: "Saved over $22K/month with zero downtime."
      },
      {
        title: "Low-Latency Model Serving for GenAI API",
        emoji: "⚡",
        challenge: "High latency, tenant routing, and dynamic billing per user.",
        solution: "KServe + Istio + Prometheus traces for multi-tenant, region-aware autoscaling.",
        impact: "Achieved 55ms p95 inference across 5 zones with usage visibility."
      },
      {
        title: "HIPAA-Compliant Infra for Medical AI",
        emoji: "🔐",
        challenge: "Launching with PHI handling and zero DevSecOps team.",
        solution: "Vault + Terraform + audit logging + OPA policies for full compliance.",
        impact: "SOC2 Type 1 + HIPAA-ready stack in under 90 days."
      }
    ]
  }
};

// SaaS Industry Data
const saasIndustry: CompleteIndustryData = {
  id: 'saas',
  hero: {
    icon: '💻',
    title: 'SaaS (Software as a Service) Cloud Engineering',
    subtitle: 'Reliable Software Delivery',
    description: 'Scale your SaaS platform with infrastructure that prioritizes reliability, compliance, and cost efficiency. We help SaaS providers automate deployments and optimize multi-tenant architectures.',
    highlights: [
      'End-to-end DevOps and SRE expertise for SaaS platforms',
      'Extensive experience with multi-tenant architectures',
      'Rapid compliance readiness for enterprise sales',
      'Cost optimization without compromising performance'
    ]
  },
  benefits: [
    "End-to-end DevOps and SRE expertise for SaaS platforms",
    "Extensive experience with multi-tenant architectures",
    "Rapid compliance readiness for enterprise sales",
    "Cost optimization without compromising performance"
  ],
  bottomCTA: {
    title: "Ready to Transform Your SaaS Platform?",
    description: "Let's discuss how our expertise can help you build a scalable, secure, and cost-efficient SaaS architecture."
  },
  components: {
    caseStudies: [
      {
        title: "Multi-Tenant DevOps for CRM SaaS Platform",
        challenge: "A fast-scaling CRM SaaS provider was struggling with downtime, manual configuration drift, and inconsistent deployments across dev, staging, and production.",
        solution: "Implemented GitOps using ArgoCD and Helm. Automated deployment rollouts with Argo Rollouts (blue-green strategy). Used Terraform and Atlantis for tenant-specific resource isolation.",
        impact: "Reduced deployment errors by 80% and achieved faster tenant provisioning.",
        metrics: [
          { value: "80%", label: "Fewer Deployment Errors" },
          { value: "3x", label: "Faster Tenant Provisioning" },
          { value: "99.9%", label: "Platform Availability" }
        ]
      },
      {
        title: "MLOps and Serverless for No-Code ML SaaS",
        challenge: "An AI-first SaaS startup offering no-code ML was facing high latency in model serving.",
        solution: "Used AWS Lambda, SageMaker, and EFS for serverless model serving. Integrated CI/CD for model retraining using GitHub Actions. Added Prometheus-based anomaly detection for models.",
        impact: "Model inference latency dropped by 40%; streamlined ML lifecycle.",
        metrics: [
          { value: "40%", label: "Latency Reduction" },
          { value: "60%", label: "ML Deployment Time" },
          { value: "12x", label: "Monitoring Coverage" }
        ]
      },
      {
        title: "Unified Observability for Fintech SaaS",
        challenge: "A Fintech SaaS lacked incident visibility and faced high MTTR.",
        solution: "Integrated Prometheus, Loki, and Grafana with SLA-based alerting. Enabled tracing using OpenTelemetry. Setup on-call routing with AlertManager + PagerDuty.",
        impact: "65% drop in MTTR with proactive alerting and improved customer trust.",
        metrics: [
          { value: "65%", label: "MTTR Reduction" },
          { value: "94%", label: "Incident Detection Rate" },
          { value: "15min", label: "Avg Response Time" }
        ]
      }
    ],
    supportStages: [
      {
        title: "Cloud Native Consulting & Advisory",
        description: "We help you design a SaaS platform strategy with scalable, secure, and cloud-native principles. From MVP to multi-region readiness, we build a roadmap tailored to your product's stage and business goals.",
        iconEmoji: "🌟"
      },
      {
        title: "Transformation & Modernization",
        description: "Our architects assess your monolithic or legacy systems and incrementally refactor them into service-oriented or microservices-based SaaS platforms using Kubernetes, containers, and modern CI/CD.",
        iconEmoji: "🔄"
      },
      {
        title: "Team Extension",
        description: "Fill DevOps, SRE, or platform gaps with Betaque's experienced SaaS engineers. Whether you need observability, GitOps, or compliance automation—we integrate with your team to boost velocity.",
        iconEmoji: "👥"
      },
      {
        title: "Coaching & Enablement",
        description: "We train your internal developers, SREs, or platform engineers on cloud-native tooling including ArgoCD, Vault, Terraform, Prometheus, and AWS/GCP best practices for SaaS.",
        iconEmoji: "🎓"
      }
    ]
  }
};

// Healthcare & Life Sciences Industry Data
const healthcareIndustry: CompleteIndustryData = {
  id: 'healthcare-life-sciences',
  hero: {
    icon: '🧬',
    title: 'Healthcare & Life Sciences',
    subtitle: 'Compliant Patient Solutions',
    description: 'HIPAA-ready infrastructure for healthcare providers and life science organizations. Our solutions accelerate research, improve patient care, and ensure the highest data security standards.',
    highlights: [
      'HIPAA-compliant systems & applications',
      'Telemedicine & remote patient monitoring',
      'Clinical data analytics & AI solutions',
      'Electronic health records (EHR) integration'
    ]
  },
  benefits: [
    "HIPAA-compliant infrastructure with audit-ready controls",
    "Secure data pipelines for clinical and genomic data",
    "Performance-optimized systems for medical imaging & diagnostics",
    "Jurisdictional compliance with global healthcare regulations"
  ],
  bottomCTA: {
    title: "Ready to Transform Your Healthcare Infrastructure?",
    description: "Let's discuss how our expertise can support your mission of improving patient care with technology."
  },
  components: {
    bottleneckSections: [
      {
        title: "Cloud & Infrastructure Bottlenecks",
        icon: "☁️",
        items: [
          {
            title: "HIPAA-Ready Cloud Architectures",
            description: "Secure infrastructure using AWS HealthLake, GCP Healthcare API, and Azure FHIR. Ensures data encryption, geo-fencing, and audit logging."
          },
          {
            title: "High-Performance Imaging & Diagnostics",
            description: "PACS rendering, genomic analysis, and ML diagnostics on GPU-enabled Kubernetes with edge caching and ETL pipelines."
          },
          {
            title: "Jurisdictional Compliance & Residency",
            description: "Region-specific deployments using Azure Confidential Compute and Sovereign Cloud models to meet regulatory mandates."
          }
        ]
      },
      {
        title: "Security & Compliance Bottlenecks",
        icon: "🔐",
        items: [
          {
            title: "PHI Protection by Design",
            description: "Vault, OPA, and encrypted storage to enforce least-privilege access, key rotation, and zero data loss."
          },
          {
            title: "Regulatory Logging & Auditing",
            description: "SIEM integration via Chronicle and Splunk, along with CloudTrail, Steampipe, and Suricata for audit readiness."
          },
          {
            title: "Policy-as-Code Governance",
            description: "Enforces encryption, tagging, backups with Kyverno, Checkov, and OPA for live policy enforcement and drift detection."
          }
        ]
      }
    ],
    techStackItems: [
      {
        title: "Cloud & Infrastructure",
        technologies: ["AWS HealthLake", "GCP Healthcare API", "Azure FHIR", "Confidential Computing", "DICOM Storage"]
      },
      {
        title: "Security & Compliance",
        technologies: ["HashiCorp Vault", "OPA", "Kyverno", "SIEM", "Encryption", "IAM"]
      },
      {
        title: "Data Processing",
        technologies: ["ETL Pipelines", "HL7/FHIR", "Healthcare ML", "Genomic Processing", "Anonymization"]
      },
      {
        title: "Integration",
        technologies: ["EHR Integration", "Telemedicine APIs", "Device Connectivity", "Clinical Workflow"]
      }
    ]
  }
};

// Retail & E-Commerce Industry Data
const retailIndustry: CompleteIndustryData = {
  id: 'retail-ecommerce',
  hero: {
    icon: '🛍️',
    title: 'Retail & E-Commerce',
    subtitle: 'Customer-Centric Commerce',
    description: 'Create seamless shopping experiences with infrastructure that scales for seasonal peaks. We help retailers build personalized customer journeys and secure payment systems that drive engagement.',
    highlights: [
      'Omnichannel retail platforms',
      'Inventory & supply chain management',
      'Personalized customer experiences',
      'AI-driven product recommendations'
    ]
  },
  benefits: [
    "Highly-available infrastructure for seasonal traffic peaks",
    "Multi-region deployments for global customer experience",
    "Secure payment processing with PCI-DSS compliance",
    "Real-time analytics for personalized customer journeys"
  ],
  bottomCTA: {
    title: "Ready to Transform Your Retail Experience?",
    description: "Let's discuss how our expertise can help you build seamless omnichannel experiences."
  },
  components: {
    bottleneckSections: [
      {
        title: "Scaling & Performance Bottlenecks",
        icon: "📈",
        items: [
          {
            title: "Scalable E-Commerce Platforms",
            description: "Kubernetes-based microservices architecture with auto-scaling for handling seasonal traffic spikes and flash sales."
          },
          {
            title: "High-Performance Storefronts",
            description: "CDN-backed, edge-optimized frontends with caching strategies for sub-second page loads and rich media."
          },
          {
            title: "Multi-Region Deployment",
            description: "Global presence with regional data compliance and localized inventory for international customers."
          }
        ]
      },
      {
        title: "Customer Experience Bottlenecks",
        icon: "👥",
        items: [
          {
            title: "Personalization Infrastructure",
            description: "Real-time recommendation engines using event streams and ML for tailored shopping experiences."
          },
          {
            title: "Omnichannel Integration",
            description: "Unified API layer for consistent inventory, pricing, and customer data across mobile, web, and in-store."
          },
          {
            title: "Search & Discovery Optimization",
            description: "Elasticsearch with ML-enhanced ranking and semantic search capabilities for product discovery."
          }
        ]
      },
      {
        title: "Security & Compliance Bottlenecks",
        icon: "🔐",
        items: [
          {
            title: "PCI-DSS Compliant Payment Processing",
            description: "Tokenization, encryption, and secure vault integration for payment protection with fraud monitoring."
          },
          {
            title: "GDPR & Privacy Compliance",
            description: "Consent management, data minimization, and automated retention policies for customer data."
          },
          {
            title: "Multi-Tenant Security",
            description: "Isolation of customer data and workloads for marketplace and multi-brand retailers."
          }
        ]
      }
    ],
    techStackItems: [
      {
        title: "Infrastructure",
        technologies: ["Kubernetes", "Redis", "CDN", "Edge Computing", "Multi-region Deployment"]
      },
      {
        title: "E-Commerce",
        technologies: ["Cart & Checkout", "Product Catalog", "Inventory Management", "Order Processing", "Payments"]
      },
      {
        title: "Analytics",
        technologies: ["Customer 360", "Recommendation Engine", "A/B Testing", "Conversion Optimization", "Customer Journey"]
      },
      {
        title: "Security",
        technologies: ["PCI Compliance", "Fraud Detection", "Data Privacy", "Identity Management"]
      }
    ]
  }
};

// Manufacturing & Industry 4.0 Data
const manufacturingIndustry: CompleteIndustryData = {
  id: 'manufacturing-industry-4',
  hero: {
    icon: '🏭',
    title: 'Manufacturing & Industry 4.0',
    subtitle: 'Smart Production Systems',
    description: 'Connect factory floors to the cloud with IoT and automation that boosts productivity. Our Industry 4.0 solutions enable real-time monitoring, predictive maintenance, and data-driven quality control.',
    highlights: [
      'IoT-enabled production monitoring',
      'Predictive maintenance systems',
      'Supply chain optimization',
      'Digital twin technology'
    ]
  },
  benefits: [
    "Edge-to-cloud infrastructure for factory floor operations",
    "Secure OT-IT integration for production systems",
    "Real-time monitoring of manufacturing KPIs",
    "AI-powered predictive maintenance to reduce downtime"
  ],
  bottomCTA: {
    title: "Ready to Modernize Your Manufacturing Operations?",
    description: "Let's discuss how our expertise can help you implement Industry 4.0 technologies."
  },
  components: {
    bottleneckSections: [
      {
        title: "Edge & IoT Bottlenecks",
        icon: "🔌",
        items: [
          {
            title: "Edge Computing Architecture",
            description: "Low-latency edge processing for real-time machine data with cloud synchronization for analytics."
          },
          {
            title: "IoT Device Management",
            description: "Secure provisioning, OTA updates, and monitoring for thousands of connected sensors and equipment."
          },
          {
            title: "OT/IT Convergence",
            description: "Secure integration between operational technology and IT systems with protocol translation and data normalization."
          }
        ]
      },
      {
        title: "Data & Analytics Bottlenecks",
        icon: "📊",
        items: [
          {
            title: "Digital Twin Implementation",
            description: "Virtual replicas of physical assets with real-time data synchronization for simulation and optimization."
          },
          {
            title: "Predictive Maintenance",
            description: "Machine learning models to predict equipment failures and schedule maintenance before downtime occurs."
          },
          {
            title: "Quality Control Automation",
            description: "Real-time visual inspection and statistical process control with computer vision and ML."
          }
        ]
      },
      {
        title: "Security & Integration Bottlenecks",
        icon: "🔐",
        items: [
          {
            title: "Industrial Network Security",
            description: "Zero-trust architecture with segmentation, monitoring, and intrusion detection for OT environments."
          },
          {
            title: "ERP & MES Integration",
            description: "Secure API-based integration between manufacturing execution systems and enterprise planning systems."
          },
          {
            title: "Supply Chain Visibility",
            description: "End-to-end tracking and traceability with blockchain and distributed ledger technologies."
          }
        ]
      }
    ],
    infraFeatures: [
      {
        title: "Edge Computing Framework",
        description: "Kubernetes at the edge for running containerized workloads close to production equipment with minimal latency."
      },
      {
        title: "Time-Series Data Platform",
        description: "Optimized storage and querying of high-volume sensor data with automated retention policies."
      },
      {
        title: "Real-Time Analytics Pipeline",
        description: "Stream processing of equipment telemetry with anomaly detection and alert generation."
      },
      {
        title: "Hybrid Connectivity",
        description: "Secure factory-to-cloud connectivity with offline operation capabilities during network disruptions."
      }
    ]
  }
};

// Energy & Utilities Industry Data
const energyIndustry: CompleteIndustryData = {
  id: 'energy-utilities',
  hero: {
    icon: '⚡',
    title: 'Energy & Utilities',
    subtitle: 'Efficient Resource Management',
    description: 'Modernize energy infrastructure with smart grid technology and sustainable resource management. We build secure monitoring systems for distributed resources and optimize energy consumption.',
    highlights: [
      'Smart grid management systems',
      'Energy consumption analytics',
      'Renewable energy integration',
      'Asset performance management'
    ]
  },
  benefits: [
    "Secure, compliant infrastructure for critical energy systems",
    "Real-time monitoring and control of distributed energy resources",
    "Advanced analytics for demand forecasting and load balancing",
    "Automated regulatory reporting and compliance"
  ],
  bottomCTA: {
    title: "Ready to Modernize Your Energy Infrastructure?",
    description: "Let's discuss how our expertise can help you build more efficient and sustainable energy systems."
  },
  components: {
    bottleneckSections: [
      {
        title: "Grid & Infrastructure Bottlenecks",
        icon: "🔌",
        items: [
          {
            title: "Smart Grid Management",
            description: "Real-time monitoring, control, and optimization of distributed energy resources and grid assets."
          },
          {
            title: "SCADA Modernization",
            description: "Secure, cloud-connected supervisory control systems with API-based integration and analytics."
          },
          {
            title: "Renewable Energy Integration",
            description: "Infrastructure for managing intermittent renewable sources, storage systems, and demand response."
          }
        ]
      },
      {
        title: "Data & Analytics Bottlenecks",
        icon: "📊",
        items: [
          {
            title: "Energy Consumption Analytics",
            description: "Real-time analysis of usage patterns, peak demand prediction, and conservation recommendations."
          },
          {
            title: "Asset Performance Management",
            description: "Predictive maintenance and health monitoring for critical infrastructure and field equipment."
          },
          {
            title: "Emissions & Sustainability Tracking",
            description: "Automated carbon accounting, ESG reporting, and sustainability metrics for regulatory compliance."
          }
        ]
      },
      {
        title: "Security & Resilience Bottlenecks",
        icon: "🔐",
        items: [
          {
            title: "Critical Infrastructure Protection",
            description: "Defense-in-depth security architecture for energy systems with continuous monitoring and threat detection."
          },
          {
            title: "Disaster Recovery & Resilience",
            description: "Multi-region failover, backup systems, and automated recovery for continuous service availability."
          },
          {
            title: "Compliance Automation",
            description: "Automated controls, auditing, and reporting for NERC CIP, FERC, and other regulatory requirements."
          }
        ]
      }
    ],
    infraFeatures: [
      {
        title: "Distributed Energy Resource Management",
        description: "Platform for monitoring and optimizing solar, wind, and battery storage across the grid."
      },
      {
        title: "Time-Series Data Platform",
        description: "High-performance storage and analytics for millions of sensors and smart meters with long-term retention."
      },
      {
        title: "Edge Computing for Utilities",
        description: "Ruggedized edge compute infrastructure for processing data at substations and remote facilities."
      },
      {
        title: "Multi-Region Resilience",
        description: "Active-active grid management systems with geographic distribution for disaster recovery."
      }
    ]
  }
};

// Export all industries data
export const allIndustriesData: { [key: string]: CompleteIndustryData } = {
  'finance-banking': finTechIndustry,
  'government-public-sector': governmentIndustry,
  'machine-learning-ai': mlaiIndustry,
  'saas': saasIndustry,
  'healthcare-life-sciences': healthcareIndustry,
  'retail-ecommerce': retailIndustry,
  'manufacturing-industry-4': manufacturingIndustry,
  'energy-utilities': energyIndustry
}; 